/**
 * Tokens for the seasonal promotion on Settings → My Plan: the summary banner,
 * the "Choose your plan" strip, the promo pills/prices on the plan cards and
 * the checkout note. Source: `Plan & Billing - Promotions.dc.html`.
 *
 * Same campaign-only exception to the brand-red rule as the dashboard promo
 * (CLAUDE.md §11.5, product decision): the peach/coral accents and the
 * Year-End orange→pink stub stay as designed, here only. Action buttons still
 * take the global button theme.
 *
 * Near-duplicates folded onto existing tokens: the mock's #8A85A0 struck price
 * → `signer.text.subtitle` (3.5:1 → 5.2:1, WCAG 1.4.3), #ECE8F2 →
 * `signer.border.main`, #EFEDF5 → `signer.border.light`, #FBFAF7 →
 * `signer.surface.hover`, #4A4462 → `signer.text.secondary`, #21143D/#24153F →
 * `signer.ink`.
 */
import { PromoCampaign } from "helpers/promotionHelpers";
import { signer, white } from "theme/colors";

export interface PlanPromoTheme {
  /** Banner + strip surface. */
  bg: string;
  /** Big figures and the "% off" accent on the dark surface. */
  accent: string;
  /** Background glyph (gift / tag) tint. */
  decoColor: string;
  /** Checkout note's coloured stub. */
  stubBg: string;
  /** Checkout note eyebrow, on white. */
  eyebrow: string;
  /** "% off" pills on light surfaces (cards, cycle toggle). */
  pillText: string;
  pillBg: string;
}

export const planPromoThemes: Record<PromoCampaign, PlanPromoTheme> = {
  bf: {
    bg: "linear-gradient(110deg, #1B1033 0%, #2A1850 55%, #3D2470 100%)",
    accent: "#FFAD80",
    decoColor: "rgba(255,255,255,0.07)",
    stubBg: signer.ink,
    eyebrow: "#B8481C", // 5.7:1 on white
    pillText: signer.ink,
    pillBg: "#EEE9F8",
  },
  ye: {
    bg: "linear-gradient(110deg, #24153F 0%, #3A1E52 55%, #5A2350 100%)",
    accent: "#FF9166",
    decoColor: "rgba(255,145,102,0.12)",
    stubBg: "linear-gradient(160deg, #F96A24 0%, #EF4868 100%)",
    eyebrow: "#C9402B", // 5.5:1 on white
    pillText: "#B8340F", // 5.6:1 on its own pill fill
    pillBg: "#FDEDE6",
  },
};

export const planPromoTokens = {
  color: {
    onDark: white,
    badgeBg: "rgba(255,255,255,0.12)",
    bannerSub: "#CFC6E3",
    offerSub: "#E4DDF2",
    offerBg: "rgba(255,255,255,0.08)",
    offerBorder: "1px solid rgba(255,255,255,0.14)",
    stripLine: "#EDE8F7",
    stripNote: "#BFB5D6",
    shadow: "0 10px 26px rgba(33,20,61,0.18)",
    struckPrice: signer.text.subtitle,
    promoTotalBg: signer.surface.hover,
    promoTotalBorder: signer.border.light,
    renewText: signer.text.secondary,
    noteBorder: signer.border.main,
    noteText: signer.ink,
    /** Unselected cycle segment: the pill sits on the grey track. */
    cyclePillIdleBg: "rgba(255,255,255,0.7)",
  },
  banner: {
    radius: "16px",
    padding: { xs: "16px", md: "16px 20px" },
    gap: "14px 24px",
    headSize: { xs: "1.0625rem", md: "1.1875rem" }, // 17 / 19
    badgeSize: "0.625rem", // 10
    offerNumSize: "1.25rem", // 20
    offerMinWidth: 130,
    decoSize: 96,
  },
  strip: {
    radius: "18px",
    padding: { xs: "20px", md: "24px 30px" },
    gap: "18px 28px",
    headSize: { xs: "1.5rem", md: "1.75rem" }, // 24 / 28
    badgeSize: "0.65625rem", // 10.5
    decoSize: { xs: 110, lg: 170 },
  },
  /** Billing-cycle segment while a campaign puts an offer pill in it. */
  cycle: {
    itemMinHeight: "38px",
    itemPaddingX: "14px",
  },
  card: {
    /** Promo headline steps up from the regular 26px price (design 28 → 34). */
    promoPriceSize: "32px",
    struckSize: "15px",
    pillSize: "10.5px",
    totalRadius: "10px",
    totalPadding: "10px 11px",
  },
  note: {
    radius: "11px",
    stubWidth: 8,
    eyebrowSize: "10px",
    lineSize: "13px",
  },
};

/** Decorative glyph behind each campaign's banner/strip — gift for Black
 *  Friday, price tag for Year-End. 120×120 viewBox, stroked in `currentColor`. */
export const planPromoDecoPaths: Record<PromoCampaign, string[]> = {
  bf: [
    "M12 38h96v22H12z",
    "M20 60v48h80V60M60 38v70",
    "M60 38c-8-18-30-24-30-10 0 8 14 10 30 10zM60 38c8-18 30-24 30-10 0 8-14 10-30 10z",
  ],
  ye: [
    "M62 10h40a8 8 0 0 1 8 8v40L58 110a8 8 0 0 1-11 0L10 73a8 8 0 0 1 0-11z",
    "M95 34a9 9 0 1 1-18 0a9 9 0 1 1 18 0z",
  ],
};
